import { Autocomplete, TextField, Theme } from '@mui/material';
import { SxProps } from '@mui/system';
import * as React from 'react';

type TypeAheadProps = {
  defaultValue?: any;
  minFilterLength: number;
  data: any[];
  placeholder: string;
  sx?: SxProps<Theme>;
  onChange: (value: any) => void;
  onFilterChange: (filterText: string | null) => void;
  getOptionLabel: (option: any) => string;
  getOptionKey?: (option: any) => string | number;
  multiple?: boolean;
  disabled?: boolean;
  error?: boolean;
  helperText?: string;
  required?: boolean;
  id?: string;
  title?: string;
};

export const filterTypeAheadOptions = <T,>(options: T[], inputValue: string, getOptionLabel: (option: T) => string): T[] => {
  const filterText = inputValue.trim().toLowerCase();
  return filterText ? options.filter((option) => getOptionLabel(option).toLowerCase().includes(filterText)) : options;
};

const TypeAhead: React.FC<React.PropsWithChildren<TypeAheadProps>> = (props) => {
  const { onChange, onFilterChange, minFilterLength, data, placeholder, getOptionLabel, getOptionKey, sx, defaultValue, disabled, id, error, helperText, required, multiple } =
    props;
  const [inputValue, setInputValue] = React.useState('');
  const getKey = (option: any) => getOptionKey?.(option) ?? getOptionLabel(option);

  const selectedLabel = multiple || !defaultValue ? '' : getOptionLabel(defaultValue);
  React.useEffect(() => {
    if (multiple) {
      return;
    }

    setInputValue(selectedLabel);
  }, [selectedLabel, multiple]);

  return (
    <Autocomplete
      multiple={multiple}
      value={defaultValue || (multiple ? [] : null)}
      sx={sx}
      id={id}
      options={data}
      disabled={disabled}
      inputValue={inputValue}
      getOptionKey={getKey}
      isOptionEqualToValue={(option, value) => getKey(option) === getKey(value)}
      filterOptions={(options, state) => filterTypeAheadOptions(options, state.inputValue, getOptionLabel)}
      onChange={(e, newValue) => {
        setInputValue('');
        onFilterChange(null);
        onChange(newValue);
      }}
      onInputChange={(e, newValue, reason) => {
        if (reason === 'reset') {
          return;
        }

        setInputValue(newValue);
        onFilterChange(newValue.length >= minFilterLength ? newValue : null);
      }}
      getOptionLabel={getOptionLabel}
      renderInput={(params) => <TextField {...params} label={placeholder} error={error} helperText={helperText} margin="normal" required={required} />}
      title={props.title}
    />
  );
};

export default TypeAhead;
